/**
 * The demo-booking calendar's real backend (GH-901), replacing GH-897/898's client-only weekday
 * math and the "fold a preferred time into the lead's message" workaround it used. Same transport
 * posture as `sales-lead.ts`: no secret in this bundle, computes nothing the server must trust.
 *
 *   GET  /v1/public/demo-availability?from=&to=   -> 200 { slots: [{ startsAt, endsAt }] }
 *   POST /v1/public/demo-bookings                 -> 201 { leadId, appointmentId, status }
 *                                                  -> 400 validation, 409 slot taken, 429 rate limit
 */
const API_BASE: string = import.meta.env["VITE_API_BASE_URL"] ?? "http://localhost:8080";

export interface OpenSlot {
  readonly startsAt: Date;
  readonly endsAt: Date;
}

export type DemoAvailabilityResult =
  { outcome: "success"; slots: readonly OpenSlot[] } | { outcome: "error"; message: string };

export async function getDemoAvailability(from: Date, to: Date): Promise<DemoAvailabilityResult> {
  let response: Response;
  try {
    const query = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
    response = await fetch(`${API_BASE}/v1/public/demo-availability?${query.toString()}`);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Could not reach the server.";
    return { outcome: "error", message: `Could not reach the server. ${message}`.trim() };
  }
  if (response.status !== 200) {
    return {
      outcome: "error",
      message: `Unexpected response from the server (${response.status}).`,
    };
  }
  const data = (await response.json()) as { slots: { startsAt: string; endsAt: string }[] };
  return {
    outcome: "success",
    slots: data.slots.map((slot) => ({
      startsAt: new Date(slot.startsAt),
      endsAt: new Date(slot.endsAt),
    })),
  };
}

export interface DemoBookingInput {
  name: string;
  email: string;
  organizationName: string;
  phone?: string;
  message?: string;
  startsAt: Date;
  endsAt: Date;
}

export interface DemoBookingResponse {
  leadId: string;
  appointmentId: string;
  status: "tentative";
}

export type DemoBookingResult =
  | { outcome: "success"; data: DemoBookingResponse }
  | { outcome: "validation_error"; message: string }
  /** The chosen slot was booked by someone else, or stopped being offered, between page load and
   *  submit — the caller should refetch availability and ask the visitor to pick again. */
  | { outcome: "slot_taken" }
  | { outcome: "rate_limited" }
  | { outcome: "error"; message: string };

async function describeValidationError(response: Response): Promise<{ message: string }> {
  try {
    const body: unknown = await response.json();
    if (body && typeof body === "object" && "error" in body) {
      const error = (body as { error: unknown }).error;
      if (
        error &&
        typeof error === "object" &&
        typeof (error as { message?: unknown }).message === "string"
      ) {
        return { message: (error as { message: string }).message };
      }
    }
  } catch {
    // Body was not JSON — fall through to the generic message below.
  }
  return { message: "Please check your details and try again." };
}

export async function submitDemoBooking(input: DemoBookingInput): Promise<DemoBookingResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/v1/public/demo-bookings`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: input.name,
        email: input.email,
        organizationName: input.organizationName,
        ...(input.phone ? { phone: input.phone } : {}),
        ...(input.message ? { message: input.message } : {}),
        startsAt: input.startsAt.toISOString(),
        endsAt: input.endsAt.toISOString(),
      }),
    });
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Could not reach the server.";
    return { outcome: "error", message: `Could not reach the server. ${message}`.trim() };
  }

  if (response.status === 201) {
    const data = (await response.json()) as DemoBookingResponse;
    return { outcome: "success", data };
  }
  if (response.status === 400) {
    const { message } = await describeValidationError(response);
    return { outcome: "validation_error", message };
  }
  if (response.status === 409) {
    return { outcome: "slot_taken" };
  }
  if (response.status === 429) {
    return { outcome: "rate_limited" };
  }
  return { outcome: "error", message: `Unexpected response from the server (${response.status}).` };
}
